import React, { useState } from 'react'
import Box from '@mui/material/Box'
import { Trans, useTranslation } from 'next-i18next'

import { EmtButton, EmtTextField, EmtTypography } from 'emt-components-v2'
import NextMuiLink from '@/components/NextMuiLink'
// import AddressAutocomplete from '@/components/EMT/AddressAutocomplete'
import EmtPhoneInput from '@/components/EmtPhoneInput'

import { Address } from '@/dataV3/Common/Address'
import { Register } from '@/modules/Auth/apiTypes'
import { AuthServiceHelper } from '@/modules/Auth'
import { useEMTDispatch } from '@/store'

import { Validator, scrollToFirstError } from '@/utils'
import { checkSignUpStringFormat, isUserOnMobile } from 'emt-library'
import { isValidPhoneNumber } from 'react-phone-number-input'

import { useFormValues, useIsResponsive } from '@/hooks'
import useEnterKey from '@/hooks/useEnterKey'
import { gtmSendCustomEvent } from '@/helpers/GTMHelper'
import { Constants } from '@/assets'
import { fbEvent } from '@rivercode/facebook-conversion-api-nextjs'
// import AddressAutocomplete from '@/components/EMT/AddressAutocomplete'

type FormValues = {
  firstName: string
  lastName: string
  email: string
  password: string
  plainPassword: string
  address: Address | null
  phone: string
  isOfAge: boolean
  tendersEnabled?: boolean | null
}

type Props = {
  onNext: (me?: any) => Promise<void> | void
  /** si tu veux préremplir */
  initialValues?: Partial<FormValues>
  loading?: boolean
  tendersEnabled?: boolean | null
  submitLabel?: string
  signupOnly?: boolean
}

const defaultValues: FormValues = {
  firstName: '',
  lastName: '',
  email: '',
  password: '',
  plainPassword: '',
  address: null,
  phone: '',
  isOfAge: true,
}

const SignUpDetailsStep: React.FC<Props> = ({
  onNext,
  initialValues,
  loading,
  tendersEnabled = null,
  submitLabel = 'Envoyer votre demande',
  signupOnly = false,
}) => {
  const { t } = useTranslation('connection')
  const { t: tc } = useTranslation('common')

  const dispatch = useEMTDispatch()
  const [isMobile] = useIsResponsive()
  const rowGap = isMobile ? '10px' : '20px'

  const [formIsSend, setFormIsSend] = useState(false)
  const [isLoading, setLoading] = useState(false)
  const [isFocus, setIsFocus] = useState(false)
  const [enterKeyRefClick] = useEnterKey()

  const { values, errors, setValue, setError, manageNotificationError, isValid } = useFormValues<FormValues>({
    ...defaultValues,
    ...initialValues,
  })

  const onFieldChange = <K extends keyof FormValues>(key: K, value: FormValues[K]) => {
    setValue(key, value)
    if (formIsSend) checkField(key, value)
  }

  const handleIsFocus = (value: boolean) => {
    if (!isUserOnMobile()) return
    setIsFocus(value)
  }

  const checkField = <K extends keyof FormValues>(key: K, value: FormValues[K] = values[key]): boolean => {
    switch (key) {
      case 'firstName':
        if (!value || checkSignUpStringFormat(value as string)) {
          setError(key, t('sign-up.details.errors.invalidFirstName'))
          return false
        }
        setError(key, '')
        return true

      case 'lastName':
        if (!value || checkSignUpStringFormat(value as string)) {
          setError(key, t('sign-up.details.errors.invalidName'))
          return false
        }
        setError(key, '')
        return true

      case 'email':
        if (!Validator.validate(value as string, Validator.defaultValidateTypes.mail)) {
          setError(key, t('sign-up.details.errors.email'))
          return false
        }
        setError(key, '')
        return true

      case 'password':
        if (value !== values.plainPassword) {
          setError('plainPassword', t('sign-up.details.errors.plainPassword'))
        } else {
          setError('plainPassword', '')
        }
        if (!Validator.validate(value as string, Validator.defaultValidateTypes.password.level2)) {
          setError(key, t('sign-up.details.errors.password'))
          return false
        }
        setError(key, '')
        return true

      case 'plainPassword':
        if (value !== values.password) {
          setError(key, t('sign-up.details.errors.plainPassword'))
          return false
        }
        setError(key, '')
        return true

      case 'address':
        if (!value) {
          setError(key, t(`create-your-profile.address-photo.errors.${key}`))
          return false
        }
        setError(key, '')
        return true

      case 'phone':
        if (!value) {
          setError(key, t('create-your-profile.address-photo.errors.phoneRequired'))
          return false
        }
        if (!Validator.validate(value as string, Validator.defaultValidateTypes.tel.i18n)) {
          setError(key, t('create-your-profile.address-photo.errors.phoneInvalid'))
          return false
        }
        if (!isValidPhoneNumber(value as string)) {
          setError(key, t('create-your-profile.address-photo.errors.phoneInvalid'))
          return false
        }
        setError(key, '')
        return true

      case 'isOfAge':
        if (!value) {
          setError(key, t('sign-up.details.errors.isOfAge'))
          return false
        }
        setError(key, '')
        return true

      default:
        return false
    }
  }

  const checkForm = () => {
    let ok = true
    ;(['firstName', 'lastName', 'email', 'password', 'plainPassword', 'phone', 'isOfAge', 'address'] as const).forEach(
      (k) => {
        if (!checkField(k)) ok = false
      },
    )
    return ok
  }

  const onSignUp = async () => {
    setFormIsSend(true)
    if (!checkForm()) {
      scrollToFirstError()
      return
    }

    try {
      setLoading(true)

      if (!values.address) {
        setError('address', t('create-your-profile.address-photo.errors.address'))
        setLoading(false)
        return
      }

      const params: Register.Body = {
        type: 'petowner',
        body: {
          firstName: values.firstName,
          lastName: values.lastName,
          email: values.email,
          password: {
            password: values.password,
            passwordConfirmation: values.plainPassword,
          },
          address: values.address,
          phone: values.phone,
          confirmTerms: values.isOfAge,
          optins: [],
          tendersEnabled: tendersEnabled,
        } satisfies Register.PetOwnerBody,
      }

      const me = await AuthServiceHelper.dispatchRegisterAsync(dispatch)(params)

      // Trackers (tu peux supprimer si inutile)
      gtmSendCustomEvent('member_registration_dogowner', {
        member_role_variable: 'maitre',
        member_id_variable: me.id,
        brand_variable: Constants.storageID,
        website_variable: Constants.rootUrl,
      })

      fbEvent({
        eventName: 'CompleteRegistration',
        emails: [values.email || ''],
        firstName: values.firstName || '',
        lastName: values.lastName || '',
        eventId: me.id,
        enableStandardPixel: true,
      })

      await onNext(me)
    } catch (error: any) {
      manageNotificationError(error)
      console.warn('Sign-Up Error', error?.message)
    } finally {
      setLoading(false)
    }
  }

  const renderSignUpTextField = (key: keyof FormValues, inputType: string) => (
    <EmtTextField
      fullWidth
      id={key}
      type={inputType}
      label={t(`sign-up.details.labels.${key}`)}
      value={values[key] as any}
      onFocus={() => handleIsFocus(true)}
      onBlur={() => handleIsFocus(false)}
      onChange={(e: React.ChangeEvent<HTMLInputElement>) => onFieldChange(key, e.target.value as any)}
      error={!!errors[key]?.length}
      helperText={errors[key]}
      onKeyDown={(e) => e.key === 'Enter' && onSignUp()}
    />
  )

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      <Box>
        {' '}
        <EmtTypography variant="h3" fontWeight="800" sx={{ fontSize: isMobile ? '24px' : '34px' }}>
          {signupOnly
            ? 'Où souhaitez-vous recevoir les offres des pet sitters ?'
            : 'Envoyez votre demande gratuitement et sans engagement'}
        </EmtTypography>{' '}
      </Box>{' '}
      <EmtTypography variant="body1" fontWeight="400">
        {signupOnly ? (
          <>
            <b>Vous serez notifié(e) par email et SMS.</b> Vous pourrez discuter avec eux de manière sécurisée via notre
            messagerie. Votre nom de famille, email et numéro de téléphone restent privés : vous choisissez avec qui et
            quand les partager.
          </>
        ) : (
          <>
            <b>Les pet sitters disponibles vous enverront un devis et vous serez prévenu(e) par email et SMS.</b> Vous
            pourrez discuter avec eux de manière sécurisée via notre messagerie. Votre nom de famille, email et numéro
            de téléphone restent privés : vous choisissez avec qui et quand les partager.
          </>
        )}
      </EmtTypography>
      {renderSignUpTextField('email', 'email')}
      <EmtPhoneInput
        error={errors.phone?.length ? errors.phone : undefined}
        onChange={(phoneNumber) => onFieldChange('phone', phoneNumber as any)}
        defaultValue={values.phone}
        value={values.phone}
        onFocus={() => handleIsFocus(true)}
        onBlur={() => handleIsFocus(false)}
      />
      <Box sx={{ display: 'flex', alignItems: 'stretch', gap: rowGap }}>
        {renderSignUpTextField('password', 'password')}
        {renderSignUpTextField('plainPassword', 'password')}
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'stretch', gap: rowGap }}>
        {renderSignUpTextField('firstName', 'text')}
        {renderSignUpTextField('lastName', 'text')}
      </Box>
      {/* <AddressAutocomplete
        label={t('create-your-profile.address-photo.labels.address')}
        address={values.address}
        onAddressChange={(address) => onFieldChange('address', address)}
        addressError={errors.address}
        onFocus={() => handleIsFocus(true)}
        onBlur={() => handleIsFocus(false)}
        onAddressError={(value) => {
          if (!values.address) setError('address', value)
        }}
        strict={true}
      /> */}
      {/* <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        <EmtCheckbox
          label={t('sign-up.details.labels.isOfAge.dogowner') as string}
          checked={values.isOfAge}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => onFieldChange('isOfAge', e.target.checked as any)}
          error={!!errors.isOfAge?.length}
          helperText={errors.isOfAge}
          variant="purple"
        />
        {renderOptins()}
      </Box> */}
      <EmtTypography
        variant="caption"
        sx={{
          color: (theme: any) => theme.palette.greyGradient[350],
          '& a': {
            color: (theme: any) => theme.palette.primary.main,
            fontWeight: 'bold',
            textDecoration: 'none',
          },
        }}
      >
        <Trans
          t={t}
          i18nKey="sign-up.details.acceptTosAndPactFormatPetsittingCallRequest"
          components={{
            LinkTOS: <NextMuiLink href={tc('footer.linksPetsitting.tos.url')} />,
          }}
        />
      </EmtTypography>
      <Box
        sx={{
          display: 'flex',
          gap: rowGap,
          ...(isMobile
            ? {
                position: 'fixed',
                bottom: '20px',
                left: '5%',
                width: '90%',
                zIndex: 1,
                display: isFocus ? 'none' : 'flex',
              }
            : {}),
        }}
        className="EmtReactNativeWebViewPaddingBottom"
      >
        <EmtButton
          ref={enterKeyRefClick}
          fullWidth
          onClick={onSignUp}
          loader={isLoading || loading}
          disabled={!isValid}
          color="purple"
        >
          {submitLabel}
        </EmtButton>
      </Box>
    </Box>
  )
}

export default SignUpDetailsStep
